import React from 'react';
import { ConversationPaneWrapper } from '../InboxPage/InboxPage.styled';
import {
    TransientConversation,
    JobInfoCard,
    useSetSelectedConversationId,
    JobApplicationEvent,
    getEncryptedConversationId,
    makeTransientConversationId,
    JobSeekerLlmFeedbackComposeActions,
    useLocale,
    type SendMessageUiState,
} from '@indeed/messaging-react';
import { TransientConversationProps } from '../InboxPage/InboxPage';
import { useHistory, useLocation } from 'react-router-dom';
import ConversationContainer from '../ConversationContainer/ConversationContainer';
import ConversationBeginEventContainer from '../ConversationBeginEventContainer';
import { ProctorCommonGroupValues, ProctorTestNames } from 'proctor';
import { isInProctorGroup } from 'utils/proctorUtils';

export const ConversationPane = ({
    conversationId,
    transientConversation,
    isSinglePane,
    navigationHandlers,
}: {
    conversationId: string | undefined;
    isSinglePane: boolean;
    transientConversation: TransientConversationProps | undefined;
    navigationHandlers?: {
        onBackClick?: () => void;
        onNavigateToJobDetails?: () => void;
    };
}) => {
    const history = useHistory();
    const location = useLocation();
    const setSelectedConversationId = useSetSelectedConversationId();
    const { language, country } = useLocale();
    const isAutoResizeEnabled = isInProctorGroup(
        ProctorTestNames.AMPL_JS_AUTOSIZE_COMPOSEBOX_TST,
        ProctorCommonGroupValues.ACTIVE
    );
    const isLlmFeedbackEnabled = isInProctorGroup(
        ProctorTestNames.AIMS_JS_LLM_FEEDBACK_TST,
        ProctorCommonGroupValues.ACTIVE
    );
    const locale = country ? `${language}_${country}` : language;

    return (
        <ConversationPaneWrapper>
            {conversationId && (
                <ConversationContainer
                    conversationId={conversationId}
                    isAutoResizeEnabled={isAutoResizeEnabled}
                    additionalComposeActions={
                        isLlmFeedbackEnabled
                            ? [
                                  <JobSeekerLlmFeedbackComposeActions
                                      key="jobseeker-llm-feedback"
                                      conversationId={conversationId}
                                      locale={locale}
                                      surface={isSinglePane ? 'mobile_web' : 'desktop_web'}
                                  />,
                              ]
                            : undefined
                    }
                    renderBeforeListComponents={[
                        <ConversationBeginEventContainer
                            key={`conversation-begin-event-${conversationId}`}
                            conversationId={conversationId}
                        />,
                        <JobInfoCard
                            key={`job-info-card-${conversationId}`}
                            conversationId={conversationId}
                        />,
                    ]}
                    navigationHandlers={isSinglePane ? navigationHandlers : undefined}
                />
            )}
            {transientConversation && !conversationId && (
                <TransientConversation
                    conversationId={makeTransientConversationId(transientConversation.jobKey)}
                    transientConversation={transientConversation}
                    isAutoResizeEnabled={isAutoResizeEnabled}
                    renderBeforeListComponents={[
                        <JobApplicationEvent
                            key={`job-application-event-new-conversation`}
                            jobKey={transientConversation.jobKey}
                            partnerId={transientConversation.jobBoardPartnerId}
                        />,
                        <JobInfoCard
                            key={`job-info-card-new-conversation`}
                            jobKey={transientConversation.jobKey}
                        />,
                    ]}
                    messageRequestsHandlers={{
                        onSendMessageSuccess: ({
                            conversationId: newConversationId,
                            uiState,
                        }: {
                            eventId: string;
                            conversationId?: string;
                            fileCount?: number;
                            uiState?: SendMessageUiState;
                        }) => {
                            if (newConversationId && uiState?.isFromActiveSelection !== false) {
                                setSelectedConversationId(newConversationId);
                                // Remove token from search params
                                const searchParams = new URLSearchParams(location.search);
                                searchParams.delete('token');
                                const cleanSearch = searchParams.toString();

                                history.replace({
                                    pathname: `/conversations/${getEncryptedConversationId(
                                        newConversationId
                                    )}`,
                                    search: cleanSearch ? `?${cleanSearch}` : '',
                                });
                            }
                        },
                    }}
                />
            )}
        </ConversationPaneWrapper>
    );
};
